import type { Agent } from '@atproto/api'
import { MACKINAC_XRPC_URL, MACKINAC_SERVICE_DID } from '@/constants/api'
import type { TrustedVerifier, VerificationRecord } from '@/types/verification'
const GET_NSID = 'social.mackinac.verification.getTrustedVerifiers'
const PUT_NSID = 'social.mackinac.verification.putTrustedVerifiers'
const RESOLVE_NSID = 'social.mackinac.verification.resolveVerifications'

// Service auth tokens are short-lived JWTs. Cache them per (agentDid, aud, lxm) to avoid
// fetching a new token on every API call (e.g. once per batch of verification lookups).
interface CachedToken {
    token: string
    expiresAt: number // ms since epoch
}

const TOKEN_EXPIRY_BUFFER_MS = 60_000 // Refresh 60s before actual expiry
const serviceAuthCache = new Map<string, CachedToken>()
// Deduplicates concurrent token fetches: if two callers miss the cache at the same time,
// the second awaits the first's in-flight promise rather than issuing a second getServiceAuth.
const inflightTokenRequests = new Map<string, Promise<string>>()

/** Parse the expiry from a JWT without a full JWT library. Returns ms since epoch, or null. */
function getJwtExpiry(token: string): number | null {
    try {
        const parts = token.split('.')
        if (parts.length < 3) return null
        const payload = parts[1]
        const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')))
        return typeof decoded.exp === 'number' ? decoded.exp * 1000 : null
    } catch {
        return null
    }
}

/** Get a service auth token, reusing a cached one if it's still fresh. */
async function getServiceAuthToken(agent: Agent, aud: string, lxm: string): Promise<string> {
    const agentDid = agent.did
    if (!agentDid) {
        // No DID available — fetch without caching
        const { data } = await agent.com.atproto.server.getServiceAuth({ aud, lxm })
        return data.token
    }

    const key = `${agentDid}:${aud}:${lxm}`
    const now = Date.now()
    const cached = serviceAuthCache.get(key)
    if (cached && cached.expiresAt - TOKEN_EXPIRY_BUFFER_MS > now) {
        return cached.token
    }

    // Deduplicate concurrent fetches: return the in-flight promise if one exists
    const inflight = inflightTokenRequests.get(key)
    if (inflight) return inflight

    const promise = agent.com.atproto.server
        .getServiceAuth({ aud, lxm })
        .then(({ data }) => {
            const expiresAt = getJwtExpiry(data.token) ?? Date.now() + 60_000
            serviceAuthCache.set(key, { token: data.token, expiresAt })
            return data.token
        })
        .finally(() => {
            inflightTokenRequests.delete(key)
        })

    inflightTokenRequests.set(key, promise)
    return promise
}

/** Evict a cached token — call when a request returns 401 so the next caller fetches a fresh one. */
function evictServiceAuthToken(agentDid: string, aud: string, lxm: string): void {
    serviceAuthCache.delete(`${agentDid}:${aud}:${lxm}`)
}

/** Clear the service auth token cache. Exposed for test isolation. */
export function clearServiceAuthCache(): void {
    serviceAuthCache.clear()
    inflightTokenRequests.clear()
}

export interface BatchVerificationResult {
    isVerified: boolean
    verifiers: VerificationRecord[]
}

export interface TrustedVerifiersData {
    verifiers: TrustedVerifier[]
    enabled: boolean
    hideAll: boolean
}

export async function fetchTrustedVerifiers(agent: Agent): Promise<TrustedVerifiersData | null> {
    try {
        const token = await getServiceAuthToken(agent, MACKINAC_SERVICE_DID, GET_NSID)

        const response = await fetch(`${MACKINAC_XRPC_URL}/xrpc/${GET_NSID}`, {
            headers: { Authorization: `Bearer ${token}` },
        })

        if (response.status === 401 && agent.did) {
            evictServiceAuthToken(agent.did, MACKINAC_SERVICE_DID, GET_NSID)
        }
        if (!response.ok) return null

        const body = await response.json()
        return {
            verifiers: Array.isArray(body.verifiers) ? body.verifiers : [],
            enabled: typeof body.enabled === 'boolean' ? body.enabled : false,
            hideAll: typeof body.hideAll === 'boolean' ? body.hideAll : false,
        }
    } catch {
        return null
    }
}

export async function saveTrustedVerifiers(
    agent: Agent,
    verifiers: TrustedVerifier[],
    flags?: { enabled?: boolean; hideAll?: boolean }
): Promise<void> {
    try {
        const token = await getServiceAuthToken(agent, MACKINAC_SERVICE_DID, PUT_NSID)

        await fetch(`${MACKINAC_XRPC_URL}/xrpc/${PUT_NSID}`, {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ verifiers, ...flags }),
        })
    } catch {
        // Silently fail — verifiers stay in localStorage as fallback
    }
}

export async function resolveVerificationsBatchOnServer(
    agent: Agent,
    identifiers: string[]
): Promise<{ [identifier: string]: BatchVerificationResult } | null> {
    if (identifiers.length === 0) return {}

    try {
        const token = await getServiceAuthToken(agent, MACKINAC_SERVICE_DID, RESOLVE_NSID)

        const params = new URLSearchParams({ identifiers: identifiers.join(',') })
        const response = await fetch(`${MACKINAC_XRPC_URL}/xrpc/${RESOLVE_NSID}?${params}`, {
            headers: { Authorization: `Bearer ${token}` },
        })

        if (response.status === 401 && agent.did) {
            evictServiceAuthToken(agent.did, MACKINAC_SERVICE_DID, RESOLVE_NSID)
        }
        if (!response.ok) return null

        const body = await response.json()
        return body.verifications ?? {}
    } catch {
        return null
    }
}
